/**
 * Relative dates for date filter values/defaults — the same tokens the backend
 * resolves in StatisticQueryService (Flowy\Core\Service\Statistic\StatisticDateToken).
 * Keep both lists in sync. Weeks start on Monday.
 */
export const DATE_TOKENS: {token: string; label: string}[] = [
    {token: '@today', label: 'Dnes'},
    {token: '@yesterday', label: 'Včera'},
    {token: '@week_start', label: 'Začiatok tohto týždňa'},
    {token: '@week_end', label: 'Koniec tohto týždňa'},
    {token: '@last_week_start', label: 'Začiatok minulého týždňa'},
    {token: '@last_week_end', label: 'Koniec minulého týždňa'},
    {token: '@month_start', label: 'Začiatok tohto mesiaca'},
    {token: '@month_end', label: 'Koniec tohto mesiaca'},
    {token: '@last_month_start', label: 'Začiatok minulého mesiaca'},
    {token: '@last_month_end', label: 'Koniec minulého mesiaca'},
    {token: '@year_start', label: 'Začiatok tohto roka'},
    {token: '@year_end', label: 'Koniec tohto roka'},
    {token: '@last_year_start', label: 'Začiatok minulého roka'},
    {token: '@last_year_end', label: 'Koniec minulého roka'},
];

export const isDateToken = (value: unknown): value is string =>
    typeof value === 'string' && DATE_TOKENS.some((item) => item.token === value);

const iso = (date: Date) => [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
].join('-');

/** YYYY-MM-DD for a token (in the viewer's local time), null for anything else. */
export const resolveDateToken = (token: string, now: Date = new Date()): string | null => {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const shift = (days: number) => new Date(today.getFullYear(), today.getMonth(), today.getDate() + days);
    const mondayOffset = (today.getDay() + 6) % 7;
    const y = today.getFullYear();
    const m = today.getMonth();
    switch (token) {
        case '@today': return iso(today);
        case '@yesterday': return iso(shift(-1));
        case '@week_start': return iso(shift(-mondayOffset));
        case '@week_end': return iso(shift(6 - mondayOffset));
        case '@last_week_start': return iso(shift(-mondayOffset - 7));
        case '@last_week_end': return iso(shift(-mondayOffset - 1));
        case '@month_start': return iso(new Date(y, m, 1));
        case '@month_end': return iso(new Date(y, m + 1, 0));
        case '@last_month_start': return iso(new Date(y, m - 1, 1));
        case '@last_month_end': return iso(new Date(y, m, 0));
        case '@year_start': return iso(new Date(y, 0, 1));
        case '@year_end': return iso(new Date(y, 11, 31));
        case '@last_year_start': return iso(new Date(y - 1, 0, 1));
        case '@last_year_end': return iso(new Date(y - 1, 11, 31));
        default: return null;
    }
};

export const dateTokenLabel = (token: string): string | null =>
    DATE_TOKENS.find((item) => item.token === token)?.label ?? null;
